import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { UserModel, UserType } from "../data/model/auth-model";
import { clearToken } from "@/core/utils/token-utils";

interface AuthContextType {
  user: UserModel | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isCoach: boolean;
  isPlayer: boolean;
  userId: string | null;
  userName: string;
  userEmail: string | null;
  login: (user: UserModel, token: string, refreshToken: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserModel | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");
      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (storedUser && storedToken) {
        try {
          const parsedUser: UserModel = JSON.parse(storedUser);
          setUser(parsedUser);
          setToken(storedToken);
          if (storedRefreshToken) {
            setRefreshToken(storedRefreshToken);
          }
        } catch (error) {
          localStorage.removeItem("user");
          clearToken();
        }
      }
    }
  }, []);

  const login = (userData: UserModel, userToken: string, userRefreshToken: string) => {
    setUser(userData);
    setToken(userToken);
    setRefreshToken(userRefreshToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", userToken);
    localStorage.setItem("refreshToken", userRefreshToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem("user");
    clearToken();
  };
  const userType = user?.type || null;
  const isAuthenticated = Boolean(token && user && userType);
  const isCoach = userType === UserType.COACH;
  const isPlayer = userType === UserType.PLAYER;
  const userId = user?.id || null;
  const userName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : "";
  const userEmail = user?.email || null;

  const value: AuthContextType = {
    user,
    token,
    refreshToken,
    isAuthenticated,
    isCoach,
    isPlayer,
    userId,
    userName,
    userEmail,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
